import React, { useCallback, useState } from "react";
import { Col } from "reactstrap";
import { FormikProps } from "formik";
import { IControlStyles, PageContent, useControlStyles } from "../../../assets/layouts";
import { Constants } from "../../../assets/utils/lib/constants";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { SortDirection } from "../../../assets/utils/lib/searchDto";
import { VfxLib } from "../../../assets/vfx";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import Control from "../../../assets/vfx/controls/control";
import Form from "../../../assets/vfx/form/form";
import { Portal } from "../../../assets/vfx/portal/portal";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { CommunityDevelopmentGridDto, SearchCommunityDevelopmentDto, SortModel } from "../../../services/dto";
import { ExpandButton } from "../../common/Buttons";
import { CommunityDevelopmentNotesGrid } from "./communityDevelopmentNotesGrid";
import { CommunityDevelopmentService } from "./communityDevelopmentService";
import { FormatHelper } from "../../../assets/utils/helpers";
import { useParams } from "react-router-dom";
import { useCommunity } from "../../../context/communityContext";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { GetApplicationSetting, IApplicationSettings } from "../../../assets/configuration";
import { useViewImageAction } from "../../../hooks/community/useViewImageAction";

interface CommunityDevelopmentPageProps {
    communityId?: string | undefined;
    controlStyles?: IControlStyles | undefined;
    onCustomEvent?: (actionType: string, dataId: string) => void;
    onDevelopmentNoteCustomEvent?: (actionType: string, dataId: string) => void;
    handleDeleteCustomDevelopment?: (dataId: string) => void;
    lookups: { responses: LookupDto[] | undefined; tags: LookupDto[] | undefined } | undefined;
}

const convertToDropdownOptions = (responses: LookupDto[] | undefined): DropdownOption[] | [] =>
    responses
        ? responses.map((response: LookupDto) => ({
            key: response.id,
            value: response.id,
            display: response.description ?? Constants.emptyString,
        }))
        : [];

const defaultSortModel: SortModel = { field: "submittedDate", direction: SortDirection.Descending };

const defaultSearchQuery: SearchCommunityDevelopmentDto = {
    isFavorite: undefined,
    pageNumber: 1,
    pageSize: 10,
    communityId: undefined,
    responseTypeId: Constants.emptyGuid,
    tagId: Constants.emptyGuid,
    sortBy: defaultSortModel,
    message: Constants.emptyString,
};

export const CommunityDevelopmentPage = (props: CommunityDevelopmentPageProps) => {
    const { communityId: paramCommunityId } = useParams();
    const { communityId } = useCommunity();
    const resolvedCommunityId: string | undefined = paramCommunityId ?? communityId ?? undefined;

    const [searchQuery, setSearchQuery] = useState<SearchCommunityDevelopmentDto>(defaultSearchQuery);
    const [isFavourite, setIsFavourite] = useState<boolean | undefined>(undefined);

    const [formResetKey, setFormResetKey] = useState(0);

    React.useEffect(() => {
        if (!resolvedCommunityId) return;
        setSearchQuery(prev =>
            prev.communityId === resolvedCommunityId
                ? prev
                : { ...prev, communityId: resolvedCommunityId, pageNumber: 1 }
        );
    }, [resolvedCommunityId]);

    const communityDevelopmentGridQuery = CommunityDevelopmentService.gridQuery({
        ...searchQuery,
        communityId: resolvedCommunityId ?? searchQuery.communityId,
        responseTypeId:
            !searchQuery.responseTypeId || searchQuery.responseTypeId === Constants.emptyGuid
                ? undefined
                : searchQuery.responseTypeId,
        tagId:
            !searchQuery.tagId || searchQuery.tagId === Constants.emptyGuid
                ? undefined
                : searchQuery.tagId,
        isFavorite: isFavourite,
    });
    const isGridLoading =
        communityDevelopmentGridQuery.isLoading || communityDevelopmentGridQuery.isFetching;
    const rows: CommunityDevelopmentGridDto[] = communityDevelopmentGridQuery?.data?.result ?? [];
    const settings: IApplicationSettings = GetApplicationSetting;
    const hostUrl: string = settings.serviceSettings.hostUrl;
    const { imageViewer, closeImage, handleViewImageAction } = useViewImageAction({
        rows,
        hostUrl,
        getId: (r) => (r?.id ? String(r.id) : undefined),
        getImagePath: (r) => r?.imagePath ?? undefined,
    });

    const handleGridCustomEvent = React.useCallback(
        (actionType: string, dataId: string) => {
            if (handleViewImageAction(actionType, dataId)) return;
            props.onCustomEvent?.(actionType, dataId);
        },
        [handleViewImageAction, props.onCustomEvent]
    );

    const styles: IControlStyles = props.controlStyles ?? useControlStyles();
    const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
    const [activeRowData, setActiveRowData] = useState<Record<string, any>>({});

    const handleSearch = (values: SearchCommunityDevelopmentDto) => {
        setSearchQuery({
            message: values.message ?? Constants.emptyString,
            responseTypeId: values.responseTypeId ?? Constants.emptyGuid,
            communityId: resolvedCommunityId ?? searchQuery.communityId,
            tagId: values.tagId ?? Constants.emptyGuid,
            isFavorite: isFavourite,
            pageSize: 10,
            pageNumber: 1,
            sortBy: defaultSortModel,
        });
    };

    const handleResponseTypeChange = (
        formikProps: FormikProps<object>,
        value: string
    ): void => {
        const values = formikProps.values as SearchCommunityDevelopmentDto;

        setSearchQuery({
            message: values.message ?? Constants.emptyString,
            responseTypeId: value || Constants.emptyGuid,
            communityId: resolvedCommunityId ?? searchQuery.communityId,
            tagId: values.tagId ?? Constants.emptyGuid,
            isFavorite: isFavourite,
            pageSize: 10,
            pageNumber: 1,
            sortBy: defaultSortModel,
        });
    };

    const handleClear = () => {
        setIsFavourite(undefined);
        setSearchQuery(prev => ({
            ...defaultSearchQuery,
            communityId: resolvedCommunityId ?? prev.communityId,
            sortBy: prev.sortBy,
            pageSize: prev.pageSize ?? 10,
            pageNumber: 1,
        }));
        setFormResetKey(k => k + 1);
    };

    const handleExpandRow = useCallback((rowData: any, rowId: string) => {
        setActiveRowData(prev => ({ ...prev, [rowId]: rowData }));
        setExpandedRowIds(prev => (prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]));
    }, []);

    const renderNestedComponents = () =>
        expandedRowIds.map(id => {
            const container = document.getElementById(id);
            if (!container) return null;

            const rowData = activeRowData[id];
            if (!rowData) return null;

            return (
                <Portal key={"notes_" + id} containerId={id}>
                    <div className="ps-3 pe-5 mb-2">
                        <CommunityDevelopmentNotesGrid
                            communityDevelopmentId={rowData?.id}
                            onNotesCustomEvent={props.onDevelopmentNoteCustomEvent}
                        />
                    </div>
                </Portal>
            );
        });

    const renderSearchComponent: React.JSX.Element = (
        <SearchContainer
            key={formResetKey}
            initialValues={{
                message: searchQuery.message ?? Constants.emptyString,
                responseTypeId: Constants.emptyGuid,
                tagId: Constants.emptyGuid,
            }}
            onSearch={handleSearch}
            onClear={handleClear}
            enableReinitialize
        >
            {(formikProps: FormikProps<object>) => (
                <>
                    <Col lg={3}>
                        <Form.Dropdown
                            name="responseTypeId"
                            label="Response Type"
                            blankOption={{
                                key: Constants.emptyGuid,
                                display: "Select Response type",
                                value: Constants.emptyGuid,
                            }}
                            options={convertToDropdownOptions(props.lookups?.responses)}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                handleResponseTypeChange(formikProps, event.target.value)
                            }
                        />
                    </Col>

                    <Col lg={2} className="ms-2">
                        <Form.Switch
                            name="isFavorite"
                            label="Favorites Only"
                            key="hasOptions"
                            checked={isFavourite}
                            onChange={(e: any) => setIsFavourite(e.target.checked ? true : undefined)}
                        />
                    </Col>
                </>
            )}
        </SearchContainer>
    );

    const renderGridComponent: React.JSX.Element = (
        <Control.DataGrid
            dataId="id"
            height="55vh"
            cols={[
                {
                    data: "id",
                    title: "ID",
                    visible: false,
                    columnType: "number",
                    alignment: "right",
                    sortable: false,
                },
                {
                    alignment: "center",
                    data: "id",
                    title: "",
                    columnType: "custom",
                    sortable: false,
                    visible: true,
                    customCell: (id: string, rowData: CommunityDevelopmentGridDto) => {
                        return (rowData?.notesCount ?? 0) > 0 ? <ExpandButton /> : <></>;
                    },
                    width: "7%",
                },
                {
                    data: "submittedDate",
                    title: "Submitted Date",
                    visible: true,
                    columnType: "custom",
                    width: "20%",
                    customCell: (id: string, rowData: CommunityDevelopmentGridDto) => {
                        if (rowData.submittedDate) {
                            return FormatHelper.formatUtcLocalDate(rowData.submittedDate)
                        }
                        return null
                    },
                    alignment: "left",
                },
                {
                    data: "message",
                    title: "Message",
                    visible: true,
                    columnType: "text",
                    alignment: "left",
                    sortable: false,
                },
                {
                    data: "tagId",
                    title: "Tag",
                    visible: true,
                    columnType: "custom",
                    alignment: "left",
                    sortable: false,
                    customCell: (id: string, rowData: CommunityDevelopmentGridDto) => {
                        if (rowData.tagId && props.lookups && props.lookups?.tags) {
                            return props.lookups?.tags.find((x) => x.id === rowData.tagId)
                                ?.display
                        }
                        return null
                    },
                },
            ]
            }
            actionCol={{
                data: "id",
                title: "Action",
                visible: true,
                columnType: "action",
                alignment: "right",
                width: "310px",
                controlStyles: styles,
                actionButtons: (row) => {
                    const buttons = VfxLib.datagridActionButtons({ datagridStyles: styles });

                    return [
                        ...(row?.imagePath ? [buttons.view_image] : []),
                        row?.isFavorite ? buttons.favorited : buttons.add_favourite,
                        buttons.edit,
                        buttons.notes,
                        buttons.delete,
                    ];
                },

            }}
            onCustomEvent={handleGridCustomEvent}
            data={communityDevelopmentGridQuery?.data?.result ?? []}
            onParentRowClick={handleExpandRow}
            totalRecords={communityDevelopmentGridQuery.data?.totalSize ?? (communityDevelopmentGridQuery.data?.result?.length ?? 0)}
            query={searchQuery}
            loading={isGridLoading}
            onQueryChange={(patch) => setSearchQuery(query => ({ ...query, ...patch }))}
            behaviorOptions={{ responsive: false }}
            uiOptions={{
                title: "Community Development",
                filterContent: renderSearchComponent,
            }}
        />
    )

    return (
        <>
            <PageContent hideHeader>
                {renderGridComponent}
                {renderNestedComponents()}
            </PageContent>
            <Modal isOpen={imageViewer.isOpen} toggle={closeImage} size="lg" centered>
                <ModalHeader toggle={closeImage}>Image</ModalHeader>
                <ModalBody>
                    {imageViewer.url ? (
                        <img
                            src={imageViewer.url}
                            alt="View"
                            style={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }}
                        />
                    ) : null}
                </ModalBody>
            </Modal>

        </>
    );
};
